/**
 * A hotel in every country: in through the revolving door to the lobby, the front desk, a lift up to everything
 * else. A room for a night or a few (a deluxe room, a suite, the penthouse), the pool and the spa on the fifth floor
 * (guests only), the rooftop bar at the top (anyone can go up for a drink), breakfast in the lobby restaurant. Or the
 * getaway: two nights in a suite, the spa and dinner, and bring your partner (it does a lot for the two of you).
 * Own the hotel (Business) and your room is on the house.
 */
import { advance, sleepUntil, spend, type LifeEvent, type LifeState } from './life.ts';
import { localPrice } from './character.ts';

const HOTELS: Record<string, string> = {
  lagos: 'The Atlantic Crest Hotel, Victoria Island', us: 'The Halcyon, Midtown Manhattan', france: 'Hôtel Lumière, Paris 8e',
  china: 'Jade Terrace Hotel, Shanghai', japan: 'Hotel Kumo, Tokyo', italy: 'Grand Hotel Aurelia, Rome', uae: 'Al Sahra Tower Hotel, Dubai',
};
export const hotelFor = (region: string): string => HOTELS[region] ?? HOTELS.lagos;

export type RoomTier = 'room' | 'suite' | 'penthouse';
export interface RoomDef { tier: RoomTier; label: string; night: number; blurb: string; floor: number }
/** (a night, in naira; elsewhere scaled to the country's prices) */
export const ROOMS: RoomDef[] = [
  { tier: 'room', label: 'Deluxe room', night: 95_000, floor: 9, blurb: 'A king bed, a rain shower, the city through a wall of glass.' },
  { tier: 'suite', label: 'Executive suite', night: 280_000, floor: 18, blurb: 'A sitting room, a deep bath by the window, breakfast included.' },
  { tier: 'penthouse', label: 'The penthouse', night: 1_400_000, floor: 31, blurb: 'The top floor: glass on three sides, a butler on call, breakfast and the spa included.' },
];
export const roomDef = (t: RoomTier) => ROOMS.find((r) => r.tier === t)!;

export interface Stay { hotel: string; tier: RoomTier; until: number; nights: number; getaway?: boolean; with?: string; spaUsed?: number; dinner?: boolean }
interface HotelState { stay?: Stay | null; stays?: number; poolDay?: number; barDay?: number; viewDay?: number; breakfastDay?: number }
const of = (s: LifeState): HotelState => ((s as LifeState & { hotel?: HotelState }).hotel ??= {});
const DAY = 1440;
const dayOf = (m: number) => Math.floor(m / DAY);

/** your hotel stay, while it lasts (checkout is noon) */
export function stayOf(s: LifeState): Stay | null {
  const st = of(s).stay;
  if (st && s.minutes >= st.until) { of(s).stay = null; return null; }
  return st ?? null;
}
export const staysHere = (s: LifeState, hotel: string) => stayOf(s)?.hotel === hotel;
/** the hotel's yours (Business): rooms on the house */
const ownsHotel = (s: LifeState, hotel: string) => ((s as LifeState & { ventures?: { name: string }[] }).ventures ?? []).some((v) => v.name === hotel);
export const isOwner = ownsHotel;

export const roomPrice = (s: LifeState, tier: RoomTier, nights: number, hotel?: string) => (hotel && ownsHotel(s, hotel) ? 0 : localPrice(s, roomDef(tier).night * nights, 1_000));
export const spaPrice = (s: LifeState) => localPrice(s, 45_000, 500);
export const cocktailPrice = (s: LifeState) => localPrice(s, 9_500, 100);
export const breakfastPrice = (s: LifeState) => localPrice(s, 18_000, 100);
export const roomServicePrice = (s: LifeState) => localPrice(s, 22_000, 100);
/** the getaway: two nights in the suite, the spa for two, dinner on the terrace (a little less than all of it apart) */
export const getawayPrice = (s: LifeState, hotel?: string) => (hotel && ownsHotel(s, hotel) ? 0 : localPrice(s, (roomDef('suite').night * 2 + 45_000 * 2 + 60_000) * 0.85, 1_000));

/** who'd come with you: your partner (or your spouse) */
export function getawayPartner(s: LifeState): string | null {
  return s.contacts.find((c) => ['Girlfriend', 'Boyfriend', 'Partner', 'Wife', 'Husband', 'Fiancée', 'Fiancé'].includes(c.relation))?.name ?? null;
}

const checkout = (s: LifeState, nights: number) => {
  // (checking in after midnight but before dawn: tonight is that night)
  const day = dayOf(s.minutes) - ((s.minutes % DAY) < 5 * 60 ? 1 : 0);
  return (day + nights) * DAY + 12 * 60;
};

export function bookRoom(s: LifeState, hotel: string, tier: RoomTier, nights: number): { ok: boolean; line: string } {
  const st = stayOf(s);
  if (st && st.hotel === hotel && st.tier === tier) {
    const price = roomPrice(s, tier, nights, hotel);
    if (price && !spend(s, price, `${roomDef(tier).label}, ${hotel.split(',')[0]} (${nights} more night${nights > 1 ? 's' : ''})`)) return { ok: false, line: 'Your card is declined.' };
    st.until += nights * DAY; st.nights += nights;
    return { ok: true, line: `Extended: ${nights} more night${nights > 1 ? 's' : ''}. Same room, same view.` };
  }
  const price = roomPrice(s, tier, nights, hotel), def = roomDef(tier);
  if (price && !spend(s, price, `${def.label}, ${hotel.split(',')[0]} (${nights} night${nights > 1 ? 's' : ''})`)) return { ok: false, line: 'Your card is declined. Maybe a smaller room?' };
  of(s).stay = { hotel, tier, until: checkout(s, nights), nights };
  const h = of(s); h.stays = (h.stays ?? 0) + 1;
  if (h.stays === 1) s.history.push({ t: s.minutes, text: `A first night at ${hotel.split(',')[0]}.` });
  return { ok: true, line: price ? `"Floor ${def.floor}. Your key card: the lift's just behind you. Checkout's at noon."` : '"Welcome back, boss. The usual: on the house." A key card slides across the marble.' };
}

export function bookGetaway(s: LifeState, hotel: string, withPartner: boolean): { ok: boolean; line: string } {
  const price = getawayPrice(s, hotel), who = withPartner ? getawayPartner(s) : null;
  if (price && !spend(s, price, `A getaway at ${hotel.split(',')[0]}`)) return { ok: false, line: 'You can’t afford the getaway yet.' };
  of(s).stay = { hotel, tier: 'suite', until: checkout(s, 2), nights: 2, getaway: true, with: who ?? undefined, spaUsed: 0 };
  const c = who ? s.contacts.find((x) => x.name === who) : null;
  if (c) c.closeness = Math.min(100, c.closeness + 10);
  s.history.push({ t: s.minutes, text: who ? `A getaway with ${who.split(' ')[0]} at ${hotel.split(',')[0]}.` : `A getaway at ${hotel.split(',')[0]}.` });
  return { ok: true, line: who ? `Two nights in the suite, the spa for two, dinner on the terrace. ${who.split(' ')[0]} squeezes your hand in the lift.` : 'Two nights in the suite, the spa, dinner on the terrace. Just you and the city.' };
}

export type HotelThing = 'pool' | 'spa' | 'bar' | 'breakfast' | 'roomservice' | 'view' | 'bath' | 'dinner';

/** what something costs you (0: included, or nothing to pay) */
export function thingPrice(s: LifeState, what: HotelThing, hotel: string): number {
  const st = staysHere(s, hotel) ? stayOf(s)! : null, owner = ownsHotel(s, hotel);
  if (owner && what !== 'bar') return 0;
  switch (what) {
    case 'spa': return st && (st.tier === 'penthouse' || (st.getaway && (st.spaUsed ?? 0) < 2)) ? 0 : spaPrice(s);
    case 'bar': return cocktailPrice(s);
    case 'breakfast': return st && st.tier !== 'room' ? 0 : breakfastPrice(s);
    case 'roomservice': return roomServicePrice(s);
    case 'dinner': return st?.getaway && !st.dinner ? 0 : localPrice(s, 60_000, 500);
    default: return 0;
  }
}

const LINES: Record<HotelThing, (s: LifeState, st: Stay | null) => string> = {
  pool: (_s, st) => `Twenty lengths in the warm blue water, then a lounger and a towel${st?.with ? `; ${st.with.split(' ')[0]} reads beside you` : ''}. The city hums below the glass.`,
  spa: (_s, st) => st?.with ? `Side by side on the tables, ${st.with.split(' ')[0]} already half asleep. An hour of hot stones and oil; you float back to the lift.` : 'An hour of hot stones and slow hands. You walk out loose, as if someone took the week off your shoulders.',
  bar: () => 'A cocktail with a sprig of something on top, the whole city lit up under you, music low.',
  breakfast: () => 'The buffet: fruit cut like jewels, eggs made in front of you, pastries, coffee that keeps coming.',
  roomservice: () => 'A knock: a trolley under a silver dome. You eat in the hotel robe with the city in the window.',
  view: () => 'You stand at the glass a long time. Cars like beads of light, the towers, the sky.',
  bath: () => 'A deep, hot bath by the window, the lights of the city floating on the water.',
  dinner: (_s, st) => st?.with ? `Dinner on the terrace by candlelight. ${st.with.split(' ')[0]} laughs at something you say and doesn't look at their phone once.` : 'Dinner on the terrace, by yourself and happy about it: three courses and the sunset.',
};

export function atHotel(s: LifeState, what: HotelThing, hotel: string): { ok: boolean; line: string; events: LifeEvent[] } {
  const st = staysHere(s, hotel) ? stayOf(s) : null, h = of(s), today = dayOf(s.minutes);
  if ((what === 'pool' || what === 'spa' || what === 'roomservice' || what === 'bath' || what === 'dinner') && !st && !ownsHotel(s, hotel)) return { ok: false, line: 'For hotel guests: book a room at the front desk.', events: [] };
  if (what === 'breakfast') { const hr = (s.minutes % DAY) / 60; if (hr < 6 || hr >= 11) return { ok: false, line: 'Breakfast is six till eleven. The restaurant is setting up for lunch.', events: [] }; }
  const price = thingPrice(s, what, hotel);
  if (price && !spend(s, price, `${{ pool: 'Pool', spa: 'Spa', bar: 'Rooftop bar', breakfast: 'Hotel breakfast', roomservice: 'Room service', view: '', bath: '', dinner: 'Dinner on the terrace' }[what]}, ${hotel.split(',')[0]}`)) return { ok: false, line: 'You can’t afford it.', events: [] };
  const n = s.needs, up = (k: keyof typeof n, v: number) => { n[k] = Math.max(0, Math.min(100, n[k] + v)); };
  let mins = 30;
  switch (what) {
    case 'pool': up('fun', h.poolDay === today ? 4 : 12); up('hygiene', 10); up('energy', -6); h.poolDay = today; mins = 45; break;
    case 'spa': up('energy', 22); up('fun', 10); up('hygiene', 15); mins = 60; if (st?.getaway) st.spaUsed = (st.spaUsed ?? 0) + 1; break;
    case 'bar': up('fun', h.barDay === today ? 5 : 10); up('social', 8); h.barDay = today; break;
    case 'breakfast': up('hunger', 55); up('fun', 4); h.breakfastDay = today; mins = 35; break;
    case 'roomservice': up('hunger', 50); up('fun', 3); mins = 40; break;
    case 'view': up('fun', h.viewDay === today ? 1 : 5); h.viewDay = today; mins = 5; break;
    case 'bath': up('hygiene', 45); up('energy', 6); up('fun', 4); mins = 40; break;
    case 'dinner': up('hunger', 60); up('fun', 10); up('social', 10); if (st) st.dinner = true; mins = 90; break;
  }
  if (st?.with && (what === 'pool' || what === 'spa' || what === 'dinner')) { const c = s.contacts.find((x) => x.name === st.with); if (c) c.closeness = Math.min(100, c.closeness + (what === 'dinner' ? 6 : 3)); up('social', 8); }
  return { ok: true, line: LINES[what](s, st), events: advance(s, mins, { busy: true }) };
}

/** the night in your room: a hotel bed sleeps well (the penthouse best), breakfast in bed if it's included */
export function sleepAtHotel(s: LifeState): LifeEvent[] {
  const st = stayOf(s);
  const ev = sleepUntil(s, 8);
  const bonus = st?.tier === 'penthouse' ? 14 : st?.tier === 'suite' ? 10 : 6;
  s.needs.energy = Math.min(100, s.needs.energy + bonus);
  s.needs.fun = Math.min(100, s.needs.fun + 4);
  if (st?.with) { const c = s.contacts.find((x) => x.name === st.with); if (c) c.closeness = Math.min(100, c.closeness + 2); }
  return ev;
}
